import { groupBy, uniq } from 'lodash/fp';

import { ReportAttributes, ReportRelationships } from 'dashboard/models/report';
import { ApiModel, ApiResource } from 'lib/interfaces/api';
import { assertNever } from 'lib/utils/types';

export interface WatchlistImage {
  'external-url'?: string;
  'asset-hash'?: string;
  caption?: string;
  'source-name'?: string;
  'source-page-url'?: string;
  'similarity-level'?: 'low' | 'medium' | 'high';
  'internal-url'?: string;
}

export enum WatchlistType {
  FitnessProbity = 'fitness-probity',
  Pep = 'pep',
  PepClass1 = 'pep-class-1',
  PepClass2 = 'pep-class-2',
  PepClass3 = 'pep-class-3',
  PepClass4 = 'pep-class-4',
  Sanction = 'sanction',
  Warning = 'warning',
}

export enum WatchlistDefaultTag {
  Legacy = 'default',
  DefaultNoPep = 'defaultnopep',
}

export function translateWatchlistType(type: WatchlistType): string {
  switch (type) {
    case WatchlistType.FitnessProbity:
      return 'Fitness & Probity';
    case WatchlistType.Pep:
    case WatchlistType.PepClass1:
    case WatchlistType.PepClass2:
    case WatchlistType.PepClass3:
    case WatchlistType.PepClass4:
      return 'Politically Exposed Persons';
    case WatchlistType.Sanction:
      return 'Sanctions';
    case WatchlistType.Warning:
      return 'Warnings';
    default:
      assertNever(type);
  }
}

export interface ResultSource {
  name?: string;
  url?: string;
}

export interface SearchResult {
  name?: string;
  aka?: string[];
  sources?: ResultSource[];
}

export enum BirthdateGranularity {
  CircaYear = 'circa_year',
  CircaMonth = 'circa_month',
  CircaDay = 'circa_day',
  Day = 'day',
  Month = 'month',
  Year = 'year',
  RangeDayToDay = 'range_day_to_day',
  RangeDayToMonth = 'range_day_to_month',
  RangeDayToYear = 'range_day_to_year',
  RangeMonthToDay = 'range_month_to_day',
  RangeMonthToMonth = 'range_month_to_month',
  RangeMonthToYear = 'range_month_to_year',
  RangeYearToDay = 'range_year_to_day',
  RangeYearToMonth = 'range_year_to_month',
  RangeYearToYear = 'range_year_to_year',
}

export interface WatchlistBirthdate {
  granularity?: string;
  day?: string;
  month?: string;
  year?: string;
  'day-start'?: string;
  'month-start'?: string;
  'year-start'?: string;
  'day-end'?: string;
  'month-end'?: string;
  'year-end'?: string;
}

const BIRTHDATE_GRANULARITY_ORDER = [
  BirthdateGranularity.CircaYear,
  BirthdateGranularity.CircaMonth,
  BirthdateGranularity.CircaDay,
  BirthdateGranularity.Day,
  BirthdateGranularity.Month,
  BirthdateGranularity.Year,
  BirthdateGranularity.RangeDayToDay,
  BirthdateGranularity.RangeDayToMonth,
  BirthdateGranularity.RangeDayToYear,
  BirthdateGranularity.RangeMonthToDay,
  BirthdateGranularity.RangeMonthToMonth,
  BirthdateGranularity.RangeMonthToYear,
  BirthdateGranularity.RangeYearToDay,
  BirthdateGranularity.RangeYearToMonth,
  BirthdateGranularity.RangeYearToYear,
];

const GRANULARITY_CIRCA = [
  BirthdateGranularity.CircaYear,
  BirthdateGranularity.CircaMonth,
  BirthdateGranularity.CircaDay,
];

const BIRTHDATE_GRANULARITY_FORMATTER: Record<
  BirthdateGranularity,
  (birthdate: WatchlistBirthdate) => string
> = {
  [BirthdateGranularity.CircaYear]: (birthdate) => birthdate.year || '',
  [BirthdateGranularity.CircaMonth]: (birthdate) => `${birthdate.year}/${birthdate.month}`,
  [BirthdateGranularity.CircaDay]: (birthdate) =>
    `${birthdate.year}/${birthdate.month}/${birthdate.day}`,
  [BirthdateGranularity.Day]: (birthdate) =>
    `${birthdate.year}/${birthdate.month}/${birthdate.day}`,
  [BirthdateGranularity.Month]: (birthdate) => `${birthdate.year}/${birthdate.month}`,
  [BirthdateGranularity.Year]: (birthdate) => birthdate.year || '',
  [BirthdateGranularity.RangeDayToDay]: (birthdate) =>
    `${birthdate['year-start']}/${birthdate['month-start']}/${birthdate['day-start']} — ${birthdate['year-end']}/${birthdate['month-end']}/${birthdate['day-end']}`,
  [BirthdateGranularity.RangeDayToMonth]: (birthdate) =>
    `${birthdate['year-start']}/${birthdate['month-start']}/${birthdate['day-start']} — ${birthdate['year-end']}/${birthdate['month-end']}`,
  [BirthdateGranularity.RangeDayToYear]: (birthdate) =>
    `${birthdate['year-start']}/${birthdate['month-start']}/${birthdate['day-start']} — ${birthdate['year-end']}`,
  [BirthdateGranularity.RangeMonthToDay]: (birthdate) =>
    `${birthdate['year-start']}/${birthdate['month-end']} — ${birthdate['year-end']}/${birthdate['month-end']}/${birthdate['day-end']}`,
  [BirthdateGranularity.RangeMonthToMonth]: (birthdate) =>
    `${birthdate['year-start']}/${birthdate['month-end']} — ${birthdate['year-end']}/${birthdate['month-end']}`,
  [BirthdateGranularity.RangeMonthToYear]: (birthdate) =>
    `${birthdate['year-start']}/${birthdate['month-end']} — ${birthdate['year-end']}`,
  [BirthdateGranularity.RangeYearToDay]: (birthdate) =>
    `${birthdate['year-start']} — ${birthdate['year-end']}/${birthdate['month-end']}/${birthdate['day-end']}`,
  [BirthdateGranularity.RangeYearToMonth]: (birthdate) =>
    `${birthdate['year-start']} — ${birthdate['year-end']}/${birthdate['month-end']}`,
  [BirthdateGranularity.RangeYearToYear]: (birthdate) =>
    `${birthdate['year-start']} — ${birthdate['year-end']}`,
};

export function translateWatchlistBirthdates(birthdates: WatchlistBirthdate[]) {
  const groupedBirthdates = groupBy('granularity', birthdates);
  const birthdatesByGranularity = BIRTHDATE_GRANULARITY_ORDER.map((granularityType) => {
    const birthdates = groupedBirthdates[granularityType] ?? [];
    const formatter = BIRTHDATE_GRANULARITY_FORMATTER[granularityType];
    return birthdates.length > 0 && formatter
      ? `${GRANULARITY_CIRCA.includes(granularityType) ? 'Circa ' : ''}${birthdates
          .map(formatter)
          .join(', ')}`
      : null;
  }).filter((dates) => dates);

  if (birthdatesByGranularity.length > 0) {
    return birthdatesByGranularity.join(', ');
  } else {
    return '';
  }
}

export function translateWatchlistBirthdatesForDisplay(birthdates?: WatchlistBirthdate[]) {
  let translatedBirthdates;
  if (birthdates != null && (translatedBirthdates = translateWatchlistBirthdates(birthdates))) {
    return [`DOB: ${translatedBirthdates}`];
  }
  return [];
}

export function translateWatchlistMatchType(matchType: WatchlistMatchType) {
  switch (matchType) {
    case 'primary_name':
      return 'Primary Name';
    case 'aka':
      return 'AKA';
    default:
      assertNever(matchType);
  }
}

export interface WatchlistLocation {
  type: string;
  address?: string;
  city?: string;
  subdivision?: string;
  'postal-code'?: string;
  country?: string;
}

export function translateWatchlistLocations({
  address,
  city,
  subdivision,
  'postal-code': postalCode,
  country,
}: WatchlistLocation) {
  return uniq([address, city, subdivision, postalCode, country]).filter(Boolean).join(', ');
}

export enum WatchlistIdentificationType {
  Passport = 'passport',
  NationalIdentification = 'national_identification_number',
  IdentificationCard = 'identification_card',
  TaxId = 'tax_id',
  Ssn = 'ssn',
  Miscellaneous = 'miscellaneous',
}

export interface WatchlistIdentification {
  type: WatchlistIdentificationType;
  country?: string;
  number?: string;
  'issue-date'?: string;
  'expiration-date'?: string;
  comment?: string;
}

export interface WatchlistDetail {
  type: string;
  detail?: string;
}

export type WatchlistMatchType = 'primary_name' | 'aka';

export interface WatchlistMatchEntity {
  name?: string;
  'name-raw'?: string | null;
  'alias-type'?: WatchlistMatchType;
  birthdate?: string;
  'birthdate-raw'?: string | null;
  birthdates?: WatchlistBirthdate[];
  title?: string;
  'group-id'?: string;
}

export interface SourceMatchEntity {
  id?: string;
  names?: string[];
  aliases?: string[];
  birthdates?: WatchlistBirthdate[];
  locations?: WatchlistLocation[];
  identifications?: WatchlistIdentification[];
  sex?: string;
  emails?: string[];
  'phone-numbers'?: string[];
  titles?: string[];
  watchlists?: WatchlistMatch[];
  'ignore-list'?: WatchlistIgnoreListEntry[];
  details?: WatchlistDetail[];
  images?: WatchlistImage[];
}

export interface WatchlistMatch {
  name?: string;
  slug?: string;
  tags?: string[];
  url?: string;
  // TODO: Remove country_code. flag_code is a more accurate
  // depictor since not all sanctions list come from countries
  'country-code'?: string;
  'flag-code'?: string;
  'list-type'?: WatchlistType;
  entities?: WatchlistMatchEntity[];
  'entities-list'?: SourceMatchEntity[];
}

export interface WatchlistIgnoreListEntry {
  'ignored-at': string;
  'run-history-completed-at': string;
  user: string;
  'ignore-reason': string;
  match: WatchlistMatch;
}

export interface WatchlistIgnoreEntityEntry {
  'entity-id': string;
  'ignored-at': string;
  'run-history-completed-at': string;
  user: string;
  'ignore-reason': string;
  match: SourceMatchEntity;
}

export interface ReportWatchlistAttributes extends ReportAttributes {
  'name-first'?: string;
  'name-middle'?: string;
  'name-last'?: string;
  term?: string;
  birthdate?: string;
  'country-code'?: string;
  'searched-lists'?: string[];
  'matched-lists'?: WatchlistMatch[];
  'entity-lists'?: SourceMatchEntity[];
  'birthdate-year'?: string;
  'searched-categories'?: WatchlistType[];
  'ignore-list'?: WatchlistIgnoreListEntry[];
  'ignore-entity-list'?: WatchlistIgnoreEntityEntry[];
  'profile-image'?: File;
  'profile-image-url'?: string;
}

export type ReportWatchlist = ApiResource<
  ReportWatchlistAttributes,
  ReportRelationships,
  ApiModel.ReportWatchlist
>;
